import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AppRemixIcon,
  AppMultiSelect,
  AppSingleSelect,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import { useParams } from 'react-router-dom';
import Translator from '../../helpers/Translator';
import _ from 'lodash';

export default function UserSelectStates(props) {
  const {
    onHandleChange,
    value,
    type = 'all',
    elements = { customStates: 'all', defaultStates: 'all' },
    isMulti = true,
    allowSelectAll = true,
    clientID = null,
    hasSelectOptions = false,
    hideNotApplicable = false,
    hideObsolete = false,
    getPermissionsFrom = 'module',
    filteredStates = [],
    title,
    placeholder,
  } = props;
  const { moduleBridgeHex } = useParams();
  const auth = useSelector((state) => state?.auth);
  const permissions = useSelector((state) =>
    getPermissionsFrom === 'client'
      ? state?.auth?.permissions?.client
      : state.module[moduleBridgeHex].permissions
  );
  const [states, setStates] = useState(null);
  const [selectedValue, setSelectedValue] = useState(undefined);

  // > LOAD STATES //
  useEffect(() => {
    if (auth && permissions) {
      const defaultStates = [];

      ['#e8e8d5', '#48b363', '#e7a163', '#e04040', '#878787', '#333333'].map(
        (color, id) => {
          if (
            !permissions?.gcl?.module?.plant?.states?.unavailableStates?.includes(
              id
            ) &&
            (elements.defaultStates === 'all' ||
              elements.defaultStates.includes(id)) &&
              (!filteredStates.includes(id))
          ) {
            if (
              id < 4 ||
              (id === 4 && !hideNotApplicable) ||
              (id == 5 && !hideObsolete)
            )
              defaultStates.push({
                value: id,
                label: ReactDOMServer.renderToString(
                  <Translator
                    file="user"
                    name={`user.obligations.state${id}`}
                  />
                ),
                circle: color,
                isCustom: false,
              });
          }
        }
      );

      if (permissions?.gcl?.module?.plant?.states?.customStates) {
        permissions.gcl.module.plant.states.customStates.map((id) => {
          if (
            elements.customStates === 'all' ||
            elements.customStates.includes(id)
          ) {
            defaultStates.push({
              value: `c${id}`,
              label: auth.customStates[id].name,
              circle: auth.customStates[id].bgColor,
              isCustom: true,
            });
          }
        });
      }

      setStates(defaultStates);
    }
  }, [auth, permissions]);
  // < LOAD STATES //

  const transformOptions = (options) => {
    if (isMulti) {
      if (options !== null) {
        return options.map((o) => ({
          id: Number.isInteger(o) ? o : parseInt(o.substring(1)),
          isCustom: !Number.isInteger(o),
        }));
      }

      return [];
    }

    if (options !== null) {
      const selectedState = states.find((s) => s.value === options);
      return {
        id: selectedState.isCustom
          ? parseInt(selectedState.value.substring(1))
          : selectedState.value,
        isCustom: selectedState.isCustom,
      };
    }

    return null;
  };

  useEffect(() => {
    if (value && states) {
      if (isMulti) {
        const valueIds = _.map(
          value.filter((v) => v.isCustom === false),
          'id'
        );
        const customValueIds = _.map(
          value.filter((v) => v.isCustom),
          'id'
        );
        const sv = states.filter(
          (s) =>
            (s.isCustom === false && valueIds.includes(s.value)) ||
            (s.isCustom &&
              customValueIds.includes(parseInt(s.value.substring(1))))
        );
        setSelectedValue(_.map(sv, 'value'));
      } else {
        setSelectedValue(value.isCustom ? `c${value.id}` : value.id);
      }
    }
  }, [value, states]);

  return isMulti ? (
    <AppMultiSelect
      options={states}
      title={
        title ??
        ReactDOMServer.renderToString(
          <Translator file="module-intelligence" name="states" />,
        )
      }
      placeholder={
        placeholder ??
        ReactDOMServer.renderToString(
          <Translator file="user" name="select.one.or.multiple.states" />,
        )
      }
      value={selectedValue || []}
      onChange={(opt) => onHandleChange(transformOptions(opt))}
      icon={<AppRemixIcon icon="apps-2" />}
      isLoading={!states}
      hasSearchBox
      hasSelectOptions={hasSelectOptions}
    />
  ) : (
    <>
      {states && (
        <AppSingleSelect
          options={states}
          title={
            title ??
            ReactDOMServer.renderToString(
              <Translator file="module-intelligence" name="states" />,
            )
          }
          placeholder={
            placeholder ??
            ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="select.state" />,
            )
          }
          value={selectedValue}
          onChange={(opt) => onHandleChange(transformOptions(opt))}
          icon={<AppRemixIcon icon="apps-2" />}
          isLoading={!states}
          hasSearchBox
          hasSelectOptions={hasSelectOptions}
        />
      )}
    </>
  );
}
